<template>
  <div class="send-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">COMPLETE</h1>
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="isAssignment"
      />
    </div>

    <!-- Main Content Layout -->
    <div class="send-interface">
      <div class="viva-container">
        <div class="send-layout">
          <!-- School Selection Panel (now centered) -->
          <div class="school-selection-panel">
            <h3 class="section-title">Send to Schools</h3>
            <div class="school-section">
              
              <!-- School Search -->
              <div class="search-section">
                <div class="form-group">
                  <input 
                    v-model="schoolFilter"
                    type="text"
                    class="form-control school-search"
                    placeholder="Search schools..."
                  >
                </div>
              </div>

              <!-- Available Schools -->
              <div class="available-schools">
                <div class="school-list">
                  <div 
                    v-for="school in filteredSchools" 
                    :key="school.orgID"
                    class="school-item"
                    @click="selectSchool(school)"
                  >
                    <i class="fa fa-university school-icon"></i>
                    <span class="school-name">{{ school.orgName }}</span>
                    <i class="fa fa-arrow-right send-arrow"></i>
                  </div>
                  
                  <div v-if="loadingSchools" class="no-schools">
                    <p>Loading schools and programs...</p>
                  </div>
                  <div v-else-if="filteredSchools.length === 0" class="no-schools">
                    <p v-if="schoolFilter">No schools found matching "{{ schoolFilter }}"</p>
                    <p v-else>No schools or programs available to send to.</p>
                  </div>
                </div>
              </div>
            </div>

              <!-- Sent Schools -->
            <div v-if="sentSchools.length > 0" class="sent-schools">
              <h4 class="subsection-title">Schools You've Sent To</h4>
              <div class="school-section">
                <div class="sent-school-list">
                  <div v-if="loadingSentSchools" class="sent-school-loading">
                    <p>Loading sent schools...</p>
                  </div>
                  <div 
                    v-else
                    v-for="school in sentSchools" 
                    :key="school.id"
                    class="sent-school-item"
                  >
                    <i class="fa fa-check-circle sent-icon"></i>
                    <span class="school-name">{{ school.name }}</span>
                    <span class="sent-date">{{ formatDate(school.dateSent) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <p class="text-link">Don't see the school you're looking for? Let us know and we can help you send it! <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a></p>
          </div>
        </div>
      </div>
    </div>

    <!-- Send Confirmation Modal -->
    <div v-if="showSendModal" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Send to {{ selectedSchool?.orgName || selectedSchool?.name }}</h2>
        </div>
        <div class="viva-modal-body">
          <div class="send-confirmation">
            <div class="school-confirm-info">
              <i class="fa fa-university large-school-icon"></i>
              <p class="selected-school-name">{{ selectedSchool?.orgName || selectedSchool?.name }}</p>
            </div>
            
            <div class="warning-section">
              <p><strong>Please note:</strong></p>
              <ul>
                <li>Once you click "Send," you cannot change or undo what has been sent.</li>
                <li>However, you can always send your Viva to additional schools and programs later.</li>
              </ul>
              <p>To proceed, please check the box below and then click "Send."</p>
            </div>

            <div class="checkbox-section">
              <label class="checkbox-container">
                <input type="checkbox" v-model="sendConfirmed">
                <span class="checkmark"></span>
                <span class="checkbox-text">
                  <strong>I understand this action cannot be undone.</strong>
                </span>
              </label>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button 
            class="viva-btn viva-btn-gray" 
            @click="cancelSend"
          >
            Cancel
          </button>
          <button
            class="viva-btn viva-btn-green"
            @click="confirmSend"
            :disabled="!sendConfirmed || sendingToSchool"
          >
            <span v-if="sendingToSchool">
              <i class="fa fa-spinner fa-spin viva-spinner--white"></i> Sending...
            </span>
            <span v-else>
              Send to {{ selectedSchool?.orgShortName || selectedSchool?.orgName || selectedSchool?.name }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Application ID Modal (optional - only shown if the org has app id types configured) -->
    <div v-if="showAppIdModal" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Application ID for {{ selectedSchool?.orgName || selectedSchool?.name }}</h2>
        </div>
        <div class="viva-modal-body">
          <p class="app-id-intro">
            If you know your application ID{{ appIdTypes.length > 1 ? 's' : '' }} for this school, you can enter
            {{ appIdTypes.length > 1 ? 'them' : 'it' }} now. You can always add it later by clicking Skip below -
            but any field marked <span class="app-id-required">*</span> is required by this school, so if you do
            enter a value for it, it needs to be entered correctly before Save will work.
          </p>
          <div v-for="type in appIdTypes" :key="type.abbr" class="form-group app-id-field">
            <label>
              {{ type.name }}
              <span v-if="type.required" class="app-id-required">*</span>
            </label>
            <input
              type="text"
              class="form-control"
              :class="{ 'app-id-locked': type.locked }"
              v-model="type.value"
              :placeholder="type.placeholder"
              :disabled="type.locked"
            >
          </div>
          <p v-if="appIdError" class="app-id-error">{{ appIdError }}</p>
        </div>
        <div class="viva-modal-footer">
          <button class="viva-btn viva-btn-gray" @click="skipAppIds">
            Skip
          </button>
          <button
            class="viva-btn viva-btn-green"
            @click="submitAppIds"
            :disabled="submittingAppIds"
          >
            <span v-if="submittingAppIds">
              <i class="fa fa-spinner fa-spin viva-spinner--white"></i> Saving...
            </span>
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="viva-modal-backdrop">
      <div class="viva-modal-content success-modal-sizing" @click.stop>
        <div class="viva-modal-header">
          <h2>Success!</h2>
        </div>
        <div class="viva-modal-body">
          <div class="success-content">
            <img src="/viva/gif-viva-3.gif" alt="Success" class="success-gif" />
            <h3 class="success-message">
              Congratulations! You have successfully sent your VIVA to {{ lastSentSchool?.orgName || lastSentSchool?.name }}.
            </h3>
            <p class="success-body-text">You may now close this tab, or send your VIVA somewhere else! You can always come back later and send to more schools by logging in at <a class="link-accent" href="https://vivaexam.com" target="_blank" rel="noopener noreferrer">vivaexam.com</a></p>

            <!-- Peer Recommendations -->
            <div v-if="peerRecommendations.length > 0" class="peer-recommendations">
              <div class="peer-header">
                <i class="fa fa-lightbulb-o peer-icon"></i>
                <h4>Hey! Other schools might also be interested:</h4>
              </div>
              <div class="peer-school-list">
                <label
                  v-for="peer in peerRecommendations"
                  :key="peer.id"
                  class="peer-school-item"
                >
                  <input type="checkbox" v-model="peer.selected">
                  <span class="peer-school-name">{{ peer.orgName || peer.name }}</span>
                </label>
              </div>
              <p class="peer-note">You don't have to have applied to send your Viva!</p>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            v-if="peerRecommendations.length > 0 && hasSelectedPeers"
            class="viva-btn viva-btn-orange"
            @click="sendToPeers"
            :disabled="sendingToPeers"
          >
            <span v-if="sendingToPeers">
              <i class="fa fa-spinner fa-spin viva-spinner--white"></i> Sending...
            </span>
            <span v-else>Send to these schools</span>
          </button>
          <button
            class="viva-btn viva-btn-orange"
            @click="openPortal"
          >
            Go to my Student Portal
          </button>
          <button
            class="viva-btn viva-btn-green"
            @click="closeSuccessModal"
          >
            Send to more schools
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  name: 'Send',
  components: {
    ProfileDropdown
  },
  data() {
    return {
      // Schools
      availableSchools: [],
      sentSchools: [],
      schoolFilter: '',

      // Send flow
      selectedSchool: null,
      showSendModal: false,
      showSuccessModal: false,
      sendConfirmed: false,
      lastSentSchool: null,

      // Peer recommendations
      peerRecommendations: [],

      // Application ID prompt (optional, shown after send if the org has app id types)
      showAppIdModal: false,
      appIdTypes: [],
      appIdError: '',
      submittingAppIds: false,

      // Loading states
      loadingSchools: false,
      loadingSentSchools: false,
      sendingToSchool: false,
      sendingToPeers: false,

      // Profile dropdown
      studentName: '',
      isAssignment: false
    }
  },

  computed: {
    filteredSchools() {
      return this.availableSchools.filter(school => 
        school.orgName.toLowerCase().includes(this.schoolFilter.toLowerCase())
      );
    },

    hasSelectedPeers() {
      return this.peerRecommendations.some(peer => peer.selected);
    },
  },

  mounted() {
    this.loadSentSchools();
    this.loadAvailableSchools();
    this.fetchStudentInfo();
  },

  methods: {
    fetchStudentInfo() {
      const statusData = getStatusData();
      if (statusData) {
        if (statusData.student_name !== undefined) {
          this.studentName = statusData.student_name;
        }
        if (statusData.is_assignment !== undefined) {
          this.isAssignment = statusData.is_assignment;
        }
      }
    },

    async loadSentSchools() {
      this.loadingSentSchools = true;
      try {
        const vparam = this.$route.query.vparam || sessionStorage.getItem('vparam');
        const response = await apiClient.get('/api/viva/sent_schools/', {
          params: { vparam }
        });
        if (response.data.status === 200) {
          // Transform API response to match component format
          this.sentSchools = response.data.orglist.map(school => ({
            id: school.orgID,
            name: school.orgName,
            dateSent: school.time,
            status: school.status
          }));
          console.log('Loaded sent schools:', this.sentSchools.length);
        } else {
          console.warn('API message:', response.data.message);
          this.sentSchools = [];
        }
      } catch (error) {
        console.error('Error loading sent schools:', error);
        this.sentSchools = [];
      } finally {
        this.loadingSentSchools = false;
      }
    },

    async loadAvailableSchools() {
      this.loadingSchools = true;
      try {
        // Load available schools that haven't been sent to
        const vparam = this.$route.query.vparam || sessionStorage.getItem('vparam');
        const response = await apiClient.get('/api/viva/available_orgs/', {
          params: { vparam, nosend: 'true' }
        });
        if (response.data.status === 200) {
          this.availableSchools = response.data.orglist || [];
          console.log('Loaded available schools:', this.availableSchools.length);
        } else {
          console.warn('API message:', response.data.message);
          this.availableSchools = [];
        }
      } catch (error) {
        console.error('Error loading schools:', error);
        this.availableSchools = [];
      } finally {
        this.loadingSchools = false;
      }
    },

    selectSchool(school) {
      this.selectedSchool = school;
      this.showSendModal = true;
      this.sendConfirmed = false;
    },

    handleModalBackdropClick() {
      // Don't allow closing by clicking backdrop
    },

    cancelSend() {
      this.showSendModal = false;
      this.selectedSchool = null;
      this.sendConfirmed = false;
    },

    async confirmSend() {
      if (!this.sendConfirmed || !this.selectedSchool || this.sendingToSchool) return;

      this.sendingToSchool = true;
      try {
        // Send to backend
        const vparam = this.$route.query.vparam || sessionStorage.getItem('vparam');
        const response = await apiClient.post('/api/viva/add_send/', {
          list: [this.selectedSchool.orgID],
          vparam: vparam
        });

        if (response.data.status === 200) {
          // Store for success modal
          this.lastSentSchool = {
            name: this.selectedSchool.orgName,
            orgName: this.selectedSchool.orgName
          };

          // Set peer recommendations from API response
          this.setPeerRecommendations(response.data.peers || []);

          // Reload data from backend to ensure consistency
          await this.loadSentSchools();
          await this.loadAvailableSchools();

          this.showSendModal = false;
          this.sendConfirmed = false;
          console.log('Successfully sent to:', this.lastSentSchool.name);

          // Optionally prompt for an application ID before showing the success modal
          await this.checkAppIdPrompt(vparam);
        } else {
          throw new Error(response.data.message || 'Send failed');
        }

      } catch (error) {
        console.error('Error sending to school:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error sending to school. Please try again.';
        alert(errorMessage);
      } finally {
        this.sendingToSchool = false;
      }
    },

    async checkAppIdPrompt(vparam) {
      try {
        const response = await apiClient.get('/api/viva/org_app_ids/', {
          params: { vparam, orgId: this.selectedSchool.orgID }
        });
        if (response.data.status === 200 && response.data.data.length > 0) {
          this.appIdTypes = response.data.data;
          this.appIdError = '';
          this.showAppIdModal = true;
          return;
        }
      } catch (error) {
        console.error('Error checking application ID types:', error);
      }
      this.showSuccessModal = true;
    },

    skipAppIds() {
      this.showAppIdModal = false;
      this.appIdTypes = [];
      this.appIdError = '';
      this.showSuccessModal = true;
    },

    async submitAppIds() {
      if (this.submittingAppIds) return;

      this.submittingAppIds = true;
      try {
        const response = await apiClient.post('/api/interview/send/appids/', {
          validate: true,
          orgAppIdTypes: this.appIdTypes
        });

        if (response.data.status === 200) {
          this.showAppIdModal = false;
          this.appIdTypes = [];
          this.appIdError = '';
          this.showSuccessModal = true;
        } else {
          this.appIdError = response.data.message || 'Please check the values entered above.';
        }
      } catch (error) {
        console.error('Error saving application id:', error);
        this.appIdError = 'Error saving application ID. Please try again or skip for now.';
      } finally {
        this.submittingAppIds = false;
      }
    },

    setPeerRecommendations(peers) {
      // Transform API peer recommendations to component format
      this.peerRecommendations = peers.map(peer => ({
        id: peer.orgID,
        orgID: peer.orgID,
        name: peer.orgName,
        orgName: peer.orgName,
        selected: false,
        logo: peer.logo,
        tagline: peer.tagline,
        url: peer.url,
        highlight: peer.highlight
      }));
    },

    async sendToPeers() {
      // Deliberately doesn't call checkAppIdPrompt like confirmSend does - a student can
      // select several peer schools at once here, and prompting for an app id per school
      // would turn one click into a string of modals. Keeping this flow fast is
      // intentional, not an oversight.
      const selectedPeers = this.peerRecommendations.filter(peer => peer.selected);

      if (selectedPeers.length === 0 || this.sendingToPeers) return;

      this.sendingToPeers = true;
      try {
        // Send to backend
        const vparam = this.$route.query.vparam || sessionStorage.getItem('vparam');
        const response = await apiClient.post('/api/viva/add_send/', {
          list: selectedPeers.map(peer => peer.orgID),
          vparam: vparam
        });

        if (response.data.status === 200) {
          // Reload data from backend to ensure consistency
          await this.loadSentSchools();
          await this.loadAvailableSchools();

          console.log('Successfully sent to peers:', selectedPeers.map(p => p.orgName));
          this.closeSuccessModal();
        } else {
          throw new Error(response.data.message || 'Peer send failed');
        }
      } catch (error) {
        console.error('Error sending to peer schools:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Error sending to schools. Please try again.';
        alert(errorMessage);
      } finally {
        this.sendingToPeers = false;
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.selectedSchool = null;
      this.lastSentSchool = null;
      this.peerRecommendations = [];
    },

    openPortal() {
      this.$router.push('/student/portal/');
    },

    formatDate(isoString) {
      const date = new Date(isoString);
      return date.toLocaleDateString();
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.send-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
}

/* Main Layout */
.send-interface {
  min-height: calc(100vh - 120px);
  padding: 40px 0;
}

.send-layout {
  display: flex;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--viva-dark);
  margin-bottom: 20px;
  text-align: center;
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--viva-dark);
  margin-bottom: 15px;
}

/* School Selection Panel */
.school-selection-panel {
  width: 100%;
  max-width: 600px;
}

.school-section {
  background: var(--viva-white);
  border: 2px solid var(--viva-green);
  border-radius: 12px;
  padding: 20px 20px 0 20px;
  margin-bottom: 10px;
}

/* Search Section */
.search-section {
  margin-bottom: 25px;
}

.school-search {
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 2px solid var(--viva-green);
  border-radius: 6px;
}

.school-search:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 5px rgba(245, 176, 65, 0.3);
}

/* School Lists */
.available-schools {
  margin-bottom: 30px;
}

.school-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.school-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;
}

.school-item:hover {
  background-color: #f8f9fa;
}

.school-item:last-child {
  border-bottom: none;
}

.school-icon {
  color: var(--viva-green);
  margin-right: 12px;
  font-size: 18px;
}

.school-name {
  flex: 1;
  font-weight: 500;
  color: var(--viva-dark);
}

.send-arrow {
  color: var(--viva-orange);
  font-size: 16px;
}

.no-schools {
  padding: 30px;
  text-align: center;
  color: var(--viva-grey);
}

/* Sent Schools */
.sent-schools {
  border-top: 2px solid #eee;
  padding-top: 20px;
}

.sent-school-list {
  border: 1px solid #ddd;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.sent-school-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
}

.sent-school-item:last-child {
  border-bottom: none;
}

.sent-icon {
  color: var(--viva-green);
  margin-right: 12px;
  font-size: 16px;
}

.sent-date {
  margin-left: auto;
  font-size: 14px;
  color: var(--viva-grey);
}

/* Application ID Modal */
.app-id-intro {
  color: var(--viva-dark);
  margin-bottom: 20px;
}

.app-id-field {
  text-align: left;
  margin-bottom: 15px;
}

.app-id-required {
  color: var(--viva-orange);
}

.app-id-error {
  color: #c0392b;
  margin-top: 10px;
}

.app-id-locked {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* Send Confirmation Modal */
.send-confirmation {
  text-align: center;
}

.school-confirm-info {
  margin-bottom: 25px;
}

.large-school-icon {
  font-size: 48px;
  color: var(--viva-green);
  margin-bottom: 15px;
}

.selected-school-name {
  font-size: 24px;
  font-weight: 600;
  color: var(--viva-dark);
  margin-bottom: 0;
}

.warning-section {
  text-align: left;
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.warning-section ul {
  margin: 10px 0;
  padding-left: 20px;
}

.warning-section li {
  margin-bottom: 5px;
}

/* Checkbox */
.checkbox-section {
  margin-bottom: 20px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.checkbox-container input {
  margin-right: 12px;
  transform: scale(1.2);
}

.checkbox-text {
  font-size: 16px;
  color: var(--viva-dark);
}

/* Success Modal */
.success-modal-sizing {
  max-height: 90vh;
  max-width: 90vw;
  width: 800px;
  overflow-y: auto;
}

.success-content {
  text-align: center;
}

/* Success GIF */
.success-gif {
  width: 40%;
  max-width: 360px;
  height: auto;
  display: block;
  margin: 0 auto;
  background-color: white;
  border-radius: 12px;
}

.success-message {
  font-size: 20px;
  margin-bottom: 12px;
  color: var(--viva-dark);
  text-align: center;
  font-weight: 600;
  line-height: 1.3;
}

.success-body-text {
  font-size: 16px;
  margin-bottom: 15px;
  color: var(--viva-dark);
  line-height: 1.4;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.viva-modal-footer .viva-btn {
  padding: 14px 20px;
  font-size: 16px;
}

/* Button spinner styles */
.viva-btn .fa-spinner {
  margin-right: 8px;
}

/* Peer Recommendations */
.peer-recommendations {
  background: rgba(245, 176, 65, 0.1);
  border-radius: 6px;
  padding: 15px 20px;
  margin-top: 12px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.peer-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}

.peer-icon {
  font-size: 20px;
  color: var(--viva-orange);
  margin-right: 8px;
}

.peer-header h4 {
  color: var(--viva-dark);
  margin: 0;
  font-size: 16px;
}

.peer-school-list {
  text-align: left;
  margin: 10px 0;
}

.peer-school-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
}

.peer-school-item input {
  margin-right: 8px;
  transform: scale(1.1);
}

.peer-school-name {
  font-weight: 500;
  color: var(--viva-dark);
  font-size: 15px;
}

.peer-note {
  font-style: italic;
  font-size: 13px;
  color: var(--viva-grey);
  margin-bottom: 0;
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .send-interface {
    padding: 20px 0;
  }

  .school-selection-panel {
    max-width: 100%;
  }

  .section-title {
    font-size: 20px;
  }

  .subsection-title {
    font-size: 16px;
  }

  .school-list {
    max-height: 250px;
  }

  .selected-school-name {
    font-size: 20px;
  }
}
</style>